<template>
  <div class="regicall-client-call">
        <span class="has-text-size-20 has-text-weight-bold">
            Belafspraak met {{call.client.title}} {{call.client.name}}
        </span>
    <span class="is-pulled-right"><i class="fal fa-phone fa-3x has-text-grey-light"></i></span>
    <br>
    <div class="mb-1 has-text-size-20 has-text-grey">
      {{call.pretty_date}} <span>&nbsp; &#183; &nbsp;</span> {{call.info_daypart.timeslot}}
    </div>

    <transition name="period-is-active-info" tag="div">
        <AnnouncementsItem
              v-if="!inCurrentPeriod"
              class="mt-2"
              v-bind:announcement="new Announcement({ message: outsidePeriodMessage})"/>
    </transition>

    <hr>
    <!-- Show information about previous call attempts -->
    <div class="attempts">
      <div v-if="!call.has_call_attempts && !startingCall">
        Nog geen belpogingen gedaan
      </div>
      <div v-else>
        <div v-for="(callAttempt, index) in completedCallAttempts"
             :key="callAttempt.id">
          <div class="columns is-marginless">
            <div class="column is-paddingless">
              <div>{{ordinal(index)}} poging</div>
            </div>
            <div class="column is-paddingless">
              {{ callAttempt.start_time }}
            </div>
            <div class="column is-paddingless has-text-weight-bold is-two-thirds" :class="callAttempt.status_color">
                            <span v-if="callAttempt.is_finished">
                              Gesprek {{ callAttempt.formattedDuration }}
                            </span>
              <span v-else>
                              {{callAttempt.status_name}}
                            </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Show active calls -->
    <div v-if="!call.has_call_attempts">
      <div v-if="startingCall" class="py-1 box is-shadowless has-background-blue-light">
                <span class="has-text-weight-semibold has-text-blue">
                    Bezig met verbinden<connecting-dots></connecting-dots>
                </span>
      </div>
      <button v-else-if="callingEnabled" class="button is-primary"
              @click="startCall()">
                <span class="icon">
                    <i class="fas fa-phone"></i>
                </span>
        <span>nu bellen</span>
      </button>
    </div>
    <div v-else-if="call.current_call_attempt">
      <call-attempt :callAttempt="call.current_call_attempt"
                    :numberOfCalls="numberOfCalls"
                    :startingCall="startingCall"
                    :callingEnabled="callingEnabled"
                    @newCall="startCall()"></call-attempt>

      <!-- If there is at least one call attempt -->
      <div v-if="call.current_call_attempt" class="mt-2">

        <!-- Notes -->
        <div class="notes">

          <!-- General note -->
          <div class="columns">
            <p class="mr-3 column is-paddingless is-one-third">
              <b>Algemene notitie</b>
              <br>
              Algemene beschrijving van het gesprek, bedoeld voor andere
              vrijwilligers.
            </p>
            <div class="column is-paddingless">
                            <textarea v-model="note"
                                      class="textarea"></textarea>
            </div>
          </div>

          <!-- Supervisor note -->
          <div class="columns">
            <p class="mr-3 column is-paddingless is-one-third">
              <b>Melding voor supervisor</b>
              <br>
              Voor wijzigingen en verdachte situaties te melden aan de supervisor.
            </p>
            <div class="column is-paddingless">
                            <textarea v-model="supervisorNote"
                                      class="textarea"></textarea>
            </div>
          </div>

          <!-- Supervisor note type -->
          <div class="columns">
            <div class="is-offset-one-third column is-paddingless">
              <div class="ml-3">
                <p>
                  <span id="supervisor-note-group-label" class="has-text-weight-bold">Supervisor notitiesoort</span>
                  <span> (optioneel)</span>
                </p>
                <div role="group" aria-labelledby="supervisor-note-group-label">
                  <b-radio v-for="category in callAlertCategories" v-bind:key="category.id"
                           v-bind:disabled="!supervisorNote.length"
                           v-model="supervisorNoteCategory"
                           v-bind:native-value="category.id">
                    {{category.name}}
                      <b-tooltip :label="category.description"
                            v-if="supervisorNote.length && category.description"
                                  type="is-dark"
                                  animated>
                        <span class="icon is-small" v-if="category.description">
                          <i class="fa fa-question-circle"></i>
                        </span>
                      </b-tooltip>
                  </b-radio>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Buttons -->
        <span class="font-bold">{{ `Wat ${lang('wil/wilt')} ${lang('je/u')} doen?` }}</span>
        <div class="mt-1 finalize-buttons">

          <div class="p-1">
            <b-button class="button"
                      @click="closeSuccess"
                      type="is-success"
                      size="is-medium"
                      icon-right="check-circle"
                      outlined
                      icon-pack="far"
                      :disabled="!call.has_finished_attempt || call.has_active_attempt">
              <div class="button-text has-text-left">
                <div class="has-text-weight-bold">Oudere <u>wel</u> gesproken</div>
                <div class="-mt-1 is-size-7 subtext">Opslaan en afspraak afsluiten</div>
              </div>
            </b-button>
          </div>
          <div class="p-1">
            <b-button class="button"
                      @click="goBack"
                      size="is-medium"
                      type="is-warning"
                      icon-right="clock"
                      icon-pack="far"
                      outlined
                      :disabled="call.has_active_attempt">
              <div class="button-text has-text-left">
                <div class="has-text-weight-bold">Straks weer proberen</div>
                <div class="-mt-1 is-size-7 subtext">Opslaan en terug naar overzicht</div>
              </div>
            </b-button>
          </div>
          <div class="p-1">
            <b-button class="button"
                      @click="closeFailure"
                      type="is-danger"
                      size="is-medium"
                      icon-right="times-circle"
                      icon-pack="far"
                      outlined
                      :disabled="call.has_active_attempt || !call.has_call_attempts">
              <div class="button-text has-text-left">
                <div class="has-text-weight-bold">Oudere <u>niet</u> gesproken</div>
                <div class="-mt-1 is-size-7 subtext">Opslaan en afspraak afsluiten</div>
              </div>
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Call, STATUS } from '../../../models/Call';
  import { CallAttempt as CallAttemptModel } from '../../../models/CallAttempt';
  import { CallAlert, CallAlertCategory } from '../../../models/CallAlert';
  import { Announcement } from '../../../models/Announcement';
  import { Info as OverviewInfo } from '../containers/Overview';
  import { Info as FollowUpInfo } from '../containers/FollowUp.vue';
  import CallAttempt from '../components/CallAttempt';
  import { capitalize, orderBy, trimStart } from 'lodash-es';
  import ConnectingDots from './ConnectingDots';
  import AnnouncementsItem from '../components/AnnouncementsItem';

  import { Promise } from 'q';

  const ORDINAL_NUMBERS = [
    'eerste',
    'tweede',
    'derde',
    'vierde',
    'vijfde',
    'zesde',
    'zevende',
    'achtste',
    'negende',
    'tiende'
  ];

  export default {
    components: {
      CallAttempt,
      ConnectingDots,
      AnnouncementsItem
    },
    props: {
      call: {
        type: Call,
        required: true
      },
      numberOfCalls: {
        type: Number
      }
    },
    mounted () {
      if (!this.call.isInCurrentPeriod) {
        this.callingEnabled = false
        if (this.call.period.secondsToStart > 0) {
          this.startOfPeriodTimer = setTimeout(() => {
            this.onPeriodStarted();
          }, this.call.period.secondsToStart * 1000);
        }
      } else {
        this.callingEnabled = true
        if (this.call.period.secondsToEnd > 0) {
          this.endOfPeriodTimer = setTimeout(() => {
            this.onPeriodExpired();
          }, this.call.period.secondsToEnd * 1000);
        }
      }
    },
    beforeDestroy () {
      clearTimeout(this.endOfPeriodTimer);
      clearTimeout(this.startOfPeriodTimer);
    },
    data: function () {
      return {
        callAlertCategory: null,
        startingCall: false,
        callingEnabled: false,
        endOfPeriodTimer: null,
        startOfPeriodTimer: null,

        Announcement
      };
    },
    computed: {
      completedCallAttempts () {
        return orderBy(this.call.callAttempts.filter(attempt => {
          return attempt.is_finished || attempt.is_failed;
        }), 'id');
      },
      callAlertCategories () {
        return CallAlertCategory.query().all();
      },
      note: {
        get: function () {
          if (this.call.callComment) {
            return this.call.callComment.comment;
          }
          return '';
        },
        set: function (comment) {
          this.call.callComment.updateComment(this.trimStart(comment));
        }
      },
      supervisorNote: {
        get: function () {
          if (this.call.callAlert) {
            return this.call.callAlert.text;
          }
          return '';
        },
        set: function (text) {
          this.call.callAlert.updateText(this.trimStart(text));
        }
      },
      supervisorNoteCategory: {
        get: function () {
          if (this.call.callAlert) {
            return this.call.callAlert.category_id;
          }
        },
        set: function (categoryId) {
          CallAlert.update({
            id: this.call.callAlert.id,
            category_id: categoryId
          });
        }
      },
      inCurrentPeriod () {
        return this.callingEnabled && this.call.isInCurrentPeriod
      },
      outsidePeriodMessage () {
        return `Er kan op dit moment geen gesprek worden gestart. Dit gesprek kan plaats vinden op ${this.call.pretty_date} tussen ${this.call.info_daypart.timeslot}`;
      }
    },
    methods: {
      trimStart,
      async startCall () {
        if (!this.callingEnabled) {
          return;
        }
        this.startingCall = true;
        const attempt = await CallAttemptModel.$create({ data: { call_id: this.call.id }});
        this.startingCall = false;
      },
      ordinal (index) {
        return capitalize(ORDINAL_NUMBERS[index]);
      },
      goBack () {
        this.saveComments()
            .then(() => {
              location.href = '/zilverlijn/overzicht';
              //this.$router.push({ name: OverviewInfo.name });
            });
      },
      goNext () {
        this.saveComments()
            .then(() => {
              this.$router.push({ name: FollowUpInfo.name, params: { id: this.call.id }});
            });
      },
      async closeSuccess () {
        await Call.$update({
          params: { id: this.call.id },
          data: { status: STATUS.FINISHED_SUCCESS }
        });
        this.goNext();
      },
      async closeFailure () {
        await Call.$update({
          params: { id: this.call.id },
          data: { status: STATUS.FINISHED_FAILED }
        });
        this.goNext();
      },
      async saveComments () {
        return new Promise(async (resolve, reject) => {
          resolve(Promise.all([
            this.call.callComment.persist(),
            this.call.callAlert.persist()
          ]));
        });
      },
      missingCategory () {
        if (this.call.callAlert) {
          return this.call.callAlert.category_id && this.call.callAlert.text;
        }
        return false;
      },
      async onPeriodStarted () {
        this.call.period.start();
        this.callingEnabled = true;
      },
      async onPeriodExpired () {
        this.call.period.end();
        this.callingEnabled = false;
      }
    }
  };
</script>

<style lang="scss">
  .regicall-client-call {
    .client-notices {
      > div {
        margin: 0.2rem 0;
      }
    }

    .attempts {
      margin: 1.6rem 0;
    }

    .notes {
      > div {
        margin: 0.8rem 0;
      }
    }

    .finalize-buttons {
      align-items: center;

      .button {
        width: 100%;
        min-width: 10rem;
        max-width: 20rem;
        justify-content: flex-start;
        &:hover {
          .subtext {
            color: inherit;
          }
        }
      }

      .button-text {
        font-size: 1rem;
        line-height: 20px;
        .subtext {
          color: hsl(0, 0%, 48%);
        }
      }
    }

    .b-radio.radio {
      margin: 0;
      padding: 0;
      width: 50%;
      height: 2.5rem;
      border-radius: 0%;

      input[type=radio] + .check {
        border-radius: 0;
        &:before {
          border-radius: 0;
        }
      }
    }

    .period-is-active-info-enter {
      opacity: 0;
      transform: scale(1.05);
    }
    .period-is-active-info-enter-to {
      transition: all 0.3s linear;
      opacity: 1;
      transform: scale(1);
    }
  }
</style>
